import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Lenis from "lenis";
import { Toaster } from "sonner";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

import Home from "@/pages/Home";
import AboutLaura from "@/pages/AboutLaura";
import Therapy from "@/pages/Therapy";
import WhatToExpect from "@/pages/WhatToExpect";
import Services from "@/pages/Services";
import FAQ from "@/pages/FAQ";
import Contact from "@/pages/Contact";
import Book from "@/pages/Book";
import Intake from "@/pages/Intake";
import Legal from "@/pages/Legal";
import NotFound from "@/pages/NotFound";

const ScrollManager = ({ lenis }) => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (lenis?.current) {
      lenis.current.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, lenis]);
  return null;
};

function App() {
  const lenisRef = useRef(null);

  useEffect(() => {
    // Skip Lenis on touch devices — its wheel-based smooth scroll interferes with native mobile scroll
    const isTouch =
      typeof window !== "undefined" &&
      (("ontouchstart" in window) || (window.matchMedia && window.matchMedia("(pointer: coarse)").matches));
    if (isTouch) {
      return () => {};
    }

    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisRef.current = lenis;

    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <ScrollManager lenis={lenisRef} />
          <Navbar />
          <main>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<AboutLaura />} />
              <Route path="/craniosacral-therapy" element={<Therapy />} />
              <Route path="/what-to-expect" element={<WhatToExpect />} />
              <Route path="/services" element={<Services />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/book" element={<Book />} />
              <Route path="/intake" element={<Intake />} />
              <Route path="/privacy" element={<Legal kind="privacy" />} />
              <Route path="/terms" element={<Legal kind="terms" />} />
              <Route path="/accessibility" element={<Legal kind="accessibility" />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          <Footer />
        </BrowserRouter>
        <Toaster position="bottom-right" richColors closeButton />
      </div>
    </HelmetProvider>
  );
}

export default App;
